/**
 * A stopped answer in the regular chat, settled against its stored row.
 *
 * Stop freezes the screen at what it shows (utils/stopFreeze.ts) and the
 * server keeps exactly that text. What the server ALSO keeps — the pages the
 * turn's searches used, the release that served it, the row's real id — only
 * reaches the screen through the post-Stop re-read (utils/stopReconcile.ts).
 * These two helpers are that re-read's regular-chat side: which row to read,
 * and what the frozen message takes from it. Never its text.
 */
import type { Message } from '@/types/models';
import type { StoppedTurn } from '@/utils/stopReconcile';

/** The message on screen that was stopped: the Continue's answer, else the
 *  given (typewriter's) message, else the last placeholder answer. */
export function stoppedMessageId(
  messages: readonly Message[], frozenId: string | undefined, continuingId: string | null,
): string | undefined {
  if (continuingId) return continuingId;
  if (frozenId) return frozenId;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role === 'assistant' && m.message_id.startsWith('temp-')) return m.message_id;
  }
  return undefined;
}

/** Which stored row is the stopped answer: the Continue's message, or the
 *  row after the turn's confirmed user message. Without either the re-read
 *  falls back to the transcript's last row. */
export function stoppedTurnOf(
  messages: readonly Message[], stoppedId: string | undefined, continuingId: string | null,
): StoppedTurn {
  if (continuingId) return { messageId: continuingId };
  const at = stoppedId ? messages.findIndex((m) => m.message_id === stoppedId) : -1;
  const asked = at > 0 ? messages[at - 1] : undefined;
  if (asked && asked.role === 'user' && !asked.message_id.startsWith('temp-')) {
    return { afterMessageId: asked.message_id };
  }
  return {};
}

/** The frozen message once its stored row is read: the text stays exactly as
 *  it is on screen — no new text may appear after a Stop — and the row adds
 *  its id (copy / branch act on it), its Sources and the release that served
 *  it. */
export function withStoredStop(shown: Message, stored: Message): Message {
  return {
    ...shown,
    message_id: stored.message_id,
    interrupted: true,
    web_sources: stored.web_sources ?? shown.web_sources,
    answered_by: stored.answered_by ?? shown.answered_by,
    model_name: stored.model_name ?? shown.model_name,
    served_by_fallback: stored.served_by_fallback ?? shown.served_by_fallback,
    created_at: stored.created_at ?? shown.created_at,
  };
}
